import { Directive, ElementRef, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Renderer2 } from '@angular/core';

export interface ImageLoadEvent {
  url: string;
  loadTime: number;
}

export interface ImageErrorEvent {
  url: string;
  error: string;
}

@Directive({
  selector: '[appLazyLoad]'
})
export class LazyLoadDirective implements OnDestroy, OnChanges {
  @Input() appLazyLoad: string = '';
  @Input() rootMargin: string = '50px';
  @Input() threshold: number = 0.1;
  @Input() enablePreload: boolean = true;
  @Input() enableCompression: boolean = true;
  @Input() compressionQuality: number = 0.8;
  @Input() aspectRatio: string = '1:1';
  @Input() enableWebP: boolean = true;

  @Output() imageLoaded = new EventEmitter<ImageLoadEvent>();
  @Output() imageError = new EventEmitter<ImageErrorEvent>();

  private observer?: IntersectionObserver;
  private loadStartTime: number = 0;
  private static imageCache: { [key: string]: { loaded: boolean; error: boolean; timestamp: number } } = {};
  private static preloadQueue: Set<string> = new Set();

  constructor(private el: ElementRef, private renderer: Renderer2) {
    this.setupIntersectionObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appLazyLoad'] && this.appLazyLoad) {
      this.resetAndReload();
    }
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private setupIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: this.rootMargin,
      threshold: this.threshold
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage();
          this.observer?.unobserve(entry.target);
        }
      });
    }, options);

    this.observer.observe(this.el.nativeElement);
  }

  private resetAndReload(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.setupIntersectionObserver();
  }

  private setupImageElement(): void {
    const img = this.el.nativeElement;
    
    // Verificar cache primeiro
    if (LazyLoadDirective.imageCache[this.appLazyLoad]) {
      const cached = LazyLoadDirective.imageCache[this.appLazyLoad];
      if (cached.loaded) {
        this.renderer.setAttribute(img, 'src', this.appLazyLoad);
        this.renderer.addClass(img, 'lazy-loaded');
        return;
      } else if (cached.error) {
        this.renderer.addClass(img, 'lazy-error');
        return;
      }
    }

    // Criar placeholder simples
    this.createSimplePlaceholder();
    this.renderer.addClass(img, 'lazy-loading');
  }

  private createSimplePlaceholder(): void {
    const img = this.el.nativeElement;
    const ratio = this.parseAspectRatio(this.aspectRatio);
    const placeholder = this.generateSimplePlaceholder(ratio);
    
    this.renderer.setAttribute(img, 'src', placeholder);
    this.renderer.addClass(img, 'lazy-placeholder');
  }

  private generateSimplePlaceholder(ratio: number): string {
    const width = 300;
    const height = Math.round(width * ratio);
    
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f8f9fa"/>
        <rect x="10%" y="10%" width="80%" height="80%" rx="8" fill="#e9ecef"/>
        <circle cx="50%" cy="50%" r="${Math.min(width, height) / 12}" 
                fill="#6c757d" opacity="0.3"/>
      </svg>
    `)}`;
  }

  private parseAspectRatio(ratio: string): number {
    const [w, h] = ratio.split(':').map(Number);
    return h / w;
  }

  private loadImage(): void {
    this.setupImageElement();
    
    const img = new Image();
    this.loadStartTime = performance.now();

    // Otimizar URL para carregamento mais rápido
    let optimizedUrl = this.appLazyLoad;
    
    if (this.enableCompression || this.enableWebP) {
      const params = new URLSearchParams();
      
      if (this.enableCompression) {
        params.set('quality', this.compressionQuality.toString());
      }
      
      if (this.enableWebP && this.supportsWebP()) {
        params.set('format', 'webp');
      }
      
      params.set('cache', 'true');
      params.set('optimize', 'true');
      
      if (params.toString()) {
        const separator = optimizedUrl.includes('?') ? '&' : '?';
        optimizedUrl = `${optimizedUrl}${separator}${params.toString()}`;
      }
    }
    
    img.onload = () => {
      this.handleImageLoad(this.appLazyLoad);
    };

    img.onerror = () => {
      this.handleImageError(this.appLazyLoad, 'Network error');
    };

    img.src = optimizedUrl;

    // Preload de próximas imagens se habilitado
    if (this.enablePreload) {
      this.preloadNextImages();
    }
  }

  private handleImageLoad(url: string): void {
    const loadTime = performance.now() - this.loadStartTime;
    
    // Atualizar cache
    LazyLoadDirective.imageCache[url] = {
      loaded: true,
      error: false,
      timestamp: Date.now()
    };

    // Atualizar elemento
    this.renderer.setAttribute(this.el.nativeElement, 'src', url);
    this.renderer.removeClass(this.el.nativeElement, 'lazy-loading');
    this.renderer.removeClass(this.el.nativeElement, 'lazy-placeholder');
    this.renderer.addClass(this.el.nativeElement, 'lazy-loaded');

    // Emitir evento
    this.imageLoaded.emit({ url, loadTime });

    // Gerenciar cache
    this.manageCache();
  }

  private handleImageError(url: string, error: string): void {
    // Atualizar cache
    LazyLoadDirective.imageCache[url] = {
      loaded: false,
      error: true,
      timestamp: Date.now()
    };

    // Atualizar elemento
    this.renderer.removeClass(this.el.nativeElement, 'lazy-loading');
    this.renderer.addClass(this.el.nativeElement, 'lazy-error');

    // Emitir evento
    this.imageError.emit({ url, error });
  }

  private preloadNextImages(): void {
    // Preload de 2-3 imagens próximas
    const nextImages = this.getNextImages();
    nextImages.slice(0, 3).forEach(imageUrl => {
      if (!LazyLoadDirective.imageCache[imageUrl] && !LazyLoadDirective.preloadQueue.has(imageUrl)) {
        this.preloadImage(imageUrl);
      }
    });
  }

  private getNextImages(): string[] {
    // Implementar lógica para obter próximas imagens
    // Por enquanto, retornar array vazio
    return [];
  }

  private preloadImage(url: string): void {
    LazyLoadDirective.preloadQueue.add(url);
    
    const img = new Image();
    img.onload = () => {
      LazyLoadDirective.imageCache[url] = {
        loaded: true,
        error: false,
        timestamp: Date.now()
      };
      LazyLoadDirective.preloadQueue.delete(url);
    };
    
    img.onerror = () => {
      LazyLoadDirective.imageCache[url] = {
        loaded: false,
        error: true,
        timestamp: Date.now()
      };
      LazyLoadDirective.preloadQueue.delete(url);
    };
    
    img.src = url;
  }

  private supportsWebP(): boolean {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  private manageCache(): void {
    const maxCacheSize = 50;
    const cacheEntries = Object.entries(LazyLoadDirective.imageCache);
    
    if (cacheEntries.length > maxCacheSize) {
      // Remover entradas mais antigas
      cacheEntries
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, cacheEntries.length - maxCacheSize)
        .forEach(([url]) => {
          delete LazyLoadDirective.imageCache[url];
        });
    }
  }

  static clearCache(): void {
    LazyLoadDirective.imageCache = {};
    LazyLoadDirective.preloadQueue.clear();
  }

  static getCacheStats(): { size: number; entries: string[] } {
    return {
      size: Object.keys(LazyLoadDirective.imageCache).length,
      entries: Object.keys(LazyLoadDirective.imageCache)
    };
  }
}
